import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../../components/Header/Header";

import {
  ButtonContainer,
  SignupButton,
  ScreenContent,
  MainContainer,
  Title,
  TextDiv,
  CreateText,
  HeaderWrapper,
  Form,
  LoginField,
  StyledMailIcon,
  Input,
  StyledLockIcon,
  StyledVisibilityOff,
  StyledVisibility,
  TitleWrapper,
  BounceDiv,
  ForgotPwContainer,
  ForgotPw,
} from "./LoginPage.styles";
import { selectedLocale } from "../../../store/slices/common/slice";
import Loader from "../../../components/Loader/Loader";
import { ReactComponent as Loading } from "../../../assets/icons/loader-white.svg";
import openToast from "../../../utils/toast";
import Footer from "../../../components/Footer/Footer";
import { isValidEmail } from "../../../utils/utils";
import { login } from "../../../store/slices/auth/asyncThunks";
import { selectLoginPending } from "../../../store/slices/auth/slice";

const LoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");

  const [visibility, setVisibility] = useState(false);
  const locale = useSelector(selectedLocale);
  const ar = locale === "ar";

  const toastId = useRef(null);

  const [checkValidation, setCheckValidation] = useState(false);
  const loginLoading = useSelector(selectLoginPending);

  const validateForm = () => {
    let formIsValid = true;

    if (!email) {
      openToast(toastId, "sign-up", "error", t("email-is-required"));
      formIsValid = false;
      return;
    } else if (!isValidEmail(email)) {
      openToast(toastId, "sign-up", "error", t("invalid-email-format"));

      formIsValid = false;
      return;
    }

    if (!password) {
      openToast(toastId, "sign-up", "error", t("password-is-required"));
      formIsValid = false;
      return;
    }

    return formIsValid;
  };

  const handleLogin = () => {
    setCheckValidation(true);
    if (validateForm()) {
      dispatch(
        login({
          email,
          password,
        })
      )
        .unwrap()
        .then((response) => {
          navigate("/dashboard");
        })
        .catch((error) => {
          openToast(toastId, "login", "error", t(error.message));
        });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  useEffect(() => {
    document.addEventListener("keypress", handleKeyPress);
    window.scrollTo(0, 0);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleKeyPress]);

  const handleVisibilityToggle = () => {
    setVisibility(!visibility);
  };

  return (
    <MainContainer>
      <HeaderWrapper>
        <Header hideLogin />
      </HeaderWrapper>

      <BounceDiv>
        <ScreenContent>
          <TitleWrapper>
            <Title>{t("welcome-to-ygii")}</Title>
            <TextDiv>
              <CreateText>{t("dont-have-an-account")}</CreateText>
              <Link to="/sign-up" className="signup">
                {t("sign-up")}
              </Link>
            </TextDiv>
          </TitleWrapper>

          <Form>
            <LoginField>
              <StyledMailIcon $ar={ar} />
              <Input
                $ar={ar}
                $error={checkValidation && !email}
                type="text"
                placeholder={t("email-address")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </LoginField>
            <LoginField>
              <StyledLockIcon $ar={ar} />
              <Input
                $ar={ar}
                type={visibility ? "text" : "password"}
                placeholder={t("password")}
                $error={checkValidation && !password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {visibility ? (
                <StyledVisibility $ar={ar} onClick={handleVisibilityToggle} />
              ) : (
                <StyledVisibilityOff
                  $ar={ar}
                  onClick={handleVisibilityToggle}
                />
              )}
            </LoginField>

            <ForgotPwContainer>
              <ForgotPw onClick={() => navigate("/forgot-password")}>
                {t("forgot-password")}
              </ForgotPw>
            </ForgotPwContainer>
          </Form>
          {!loginLoading ? (
            <ButtonContainer>
              <SignupButton onClick={handleLogin}>{t("log-in")}</SignupButton>
            </ButtonContainer>
          ) : (
            <ButtonContainer>
              <SignupButton disabled>
                <Loading height={30} />
              </SignupButton>
            </ButtonContainer>
          )}
        </ScreenContent>
      </BounceDiv>
      <Footer ar={ar} />
      {loginLoading && <Loader />}
    </MainContainer>
  );
};

export default LoginPage;
